import React, { FC } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import Slider from 'react-slick';
import useSlickCarousel from 'hooks/useSlickCarousel';
import findAvailableLocale from 'models/helpers/findAvailableLocale';
import { selectLocale } from 'models/localization';
import BannerSwitcher from './BannerSwitcher';

const StyledSlider = styled(Slider)<{ $maxSliderHeight: number }>`
  width: 100%;
  height: 100%;
  overflow: hidden;
  max-height: ${({ $maxSliderHeight }) => $maxSliderHeight}px;

  & * {
    width: 100%;
    height: 100%;
  }
`;

const AssetContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  text-align: center;
  justify-content: center;
`;

const SliderBulletList = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 8px 0;
`;

const SliderBulletContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
`;

const SliderBullet = styled.div<{ $active: boolean }>`
  width: 8px;
  height: 2px;
  background: #484848;
  opacity: ${({ $active }) => ($active ? 1 : 0.2)};
  border-radius: 4px;
`;

export interface BannerCarouselProps {
  banners: any[];
  maxSliderHeight?: number;
  bullet?: boolean;
}

const BannerCarousel: FC<BannerCarouselProps> = ({ banners, maxSliderHeight, bullet }) => {
  const {
    activeIndex,
    sliderRef,
    postSliderChange,
    pauseSlider,
    playSlider,
  } = useSlickCarousel();
  const locale = useSelector(selectLocale);

  return (
    <>
      <StyledSlider
        autoplay={banners.length > 1}
        ref={sliderRef}
        arrows={false}
        dots={false}
        infinite
        $maxSliderHeight={maxSliderHeight}
        pauseOnHover={false}
        afterChange={postSliderChange}
      >
        {banners.map(({
          id,
          type,
          src,
          htmlId,
          autoPlay,
          redirectUrl = {},
          redirectExternal,
          chatroomId,
          chatroomName,
          objectFit,
        }, index) => (
          <AssetContainer key={`AssetContainer-${id || index}`}>
            <BannerSwitcher
              type={type}
              objectFit={objectFit}
              src={findAvailableLocale(src, locale)}
              htmlId={htmlId}
              autoPlay={autoPlay}
              redirectUrl={findAvailableLocale(redirectUrl, locale)}
              redirectExternal={redirectExternal}
              chatroomId={chatroomId}
              chatroomName={chatroomName}
              activeIndex={activeIndex}
              index={index}
              onVideoPlay={pauseSlider}
              onVideoStop={playSlider}
            />
          </AssetContainer>
        ))}
      </StyledSlider>
      {bullet && (
        <SliderBulletList>
          {banners.map((_, index) => (
            <SliderBulletContainer key={`MobileBullet-${index}`}>
              <SliderBullet $active={index === activeIndex} />
            </SliderBulletContainer>
          ))}
        </SliderBulletList>
      )}
    </>
  );
};

export default BannerCarousel;
